import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../../app/components/ui/resizable";

type PosLayoutProps = {
  topBar: ReactNode;
  productPanel: ReactNode;
  /** Omitted on phone when cart lives in a `Drawer` instead. */
  cartPanel: ReactNode | null;
  /** When true, stacked layout (&lt;1024px) omits cart — phone uses an external Drawer. */
  omitTabletStackCart?: boolean;
};

function useIsLgUp() {
  const [lgUp, setLgUp] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setLgUp(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return lgUp;
}

export function PosLayout({ topBar, productPanel, cartPanel, omitTabletStackCart }: PosLayoutProps) {
  const lgUp = useIsLgUp();

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-screen-2xl flex-col bg-muted/40">
      <div className="shrink-0">{topBar}</div>

      {lgUp ? (
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId="uplevo-pos-v2-panels"
          className="flex min-h-0 flex-1"
        >
          <ResizablePanel defaultSize={70} minSize={40} className="min-h-0 min-w-0">
            {productPanel}
          </ResizablePanel>
          <ResizableHandle withHandle className="w-3 shrink-0 bg-transparent px-1" />
          <ResizablePanel defaultSize={30} minSize={22} className="flex min-h-0 min-w-0 flex-col overflow-hidden p-3 sm:p-4">
            {cartPanel}
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{productPanel}</div>
          {!omitTabletStackCart ? (
            <div className="flex min-h-0 w-full shrink-0 flex-col overflow-hidden border-t border-border p-4 sm:p-6">
              {cartPanel}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
